import { Field } from '@ark-ui/react/field';
import { css, cva, cx } from '@repo/panda/css';
import type React from 'react';
import { generateErrorId, generateUniqueId } from '../utils/accessibility';
import { Type } from './type';

type ArkTextAreaProps = React.ComponentProps<typeof Field.Textarea>;

interface CustomTextAreaProps {
    errorText?: string;
    fullWidth?: boolean;
    size?: 'sm' | 'md' | 'lg' | 'xl';
    variant?: 'dark' | 'light';
    helperText?: string;
    resize?: 'none' | 'vertical' | 'horizontal' | 'both';
    label?: string;
    required?: boolean;
}

export interface TextAreaProps extends Omit<ArkTextAreaProps, 'className'>, CustomTextAreaProps {
    className?: string;
}

const textAreaStyles = cva({
    base: {
        width: 'full',
        borderRadius: '12',
        border: '[1px solid transparent]',
        transition: '[all 0.2s ease]',
        outline: 'none',
        cursor: 'text',
        _disabled: {
            opacity: 0.5,
            cursor: 'not-allowed',
        },
    },
    variants: {
        variant: {
            dark: {
                backgroundColor: 'whiteAlpha.5',
                color: 'whiteAlpha.80',
                _focusVisible: {
                    backgroundColor: 'whiteAlpha.10',
                },
                _hover: {
                    backgroundColor: 'whiteAlpha.10',
                },
                _placeholder: {
                    color: 'whiteAlpha.30',
                },
            },
            light: {
                backgroundColor: 'wemod.black.10',
                color: 'wemod.black.80',
                _focusVisible: {
                    backgroundColor: 'wemod.black.20',
                },
                _hover: {
                    backgroundColor: 'wemod.black.20',
                },
                _placeholder: {
                    color: 'wemod.black.50',
                },
            },
        },
        size: {
            sm: {
                padding: '[8px 12px]',
                fontSize: 'md',
                minHeight: '[80px]',
            },
            md: {
                padding: '[12px 16px]',
                fontSize: 'lg',
                minHeight: '[100px]',
            },
            lg: {
                padding: '[16px 20px]',
                fontSize: 'xl',
                minHeight: '[120px]',
            },
            xl: {
                padding: '[20px 24px]',
                fontSize: '2xl',
                minHeight: '[140px]',
            },
        },
        resize: {
            none: { resize: 'none' },
            vertical: { resize: 'vertical' },
            horizontal: { resize: 'horizontal' },
            both: { resize: 'both' },
        },
    },
    defaultVariants: {
        variant: 'dark',
        size: 'md',
        resize: 'vertical',
    },
});

const errorStyles = cva({
    base: {
        fontSize: 'sm',
        marginTop: '4',
    },
    variants: {
        variant: {
            dark: { color: 'error' },
            light: { color: 'error.600' },
        },
    },
    defaultVariants: {
        variant: 'dark',
    },
});

const helperTextStyles = cva({
    base: {
        fontSize: 'sm',
        marginTop: '4',
    },
    variants: {
        variant: {
            dark: { color: 'wemod.black.50' },
            light: { color: 'wemod.black.60' },
        },
    },
    defaultVariants: {
        variant: 'dark',
    },
});

const labelStyles = cva({
    base: {},
    variants: {
        required: {
            true: {
                _after: {
                    content: '" *"',
                    color: 'error',
                },
            },
        },
    },
    defaultVariants: {
        required: false,
    },
});

export const TextArea = ({
    errorText,
    helperText,
    fullWidth = false,
    size = 'md',
    variant = 'dark',
    resize = 'vertical',
    className,
    label,
    required = false,
    'aria-describedby': ariaDescribedBy,
    ...arkProps
}: TextAreaProps) => {
    const hasError = Boolean(errorText);
    const hasHelperText = Boolean(helperText);
    const hasLabel = Boolean(label);

    // Generate IDs for accessibility
    const errorId = generateErrorId('text-area', hasError, ariaDescribedBy);
    // Generate unique ID for the textarea if label is provided
    const textAreaId = hasLabel ? generateUniqueId('text-area') : arkProps.id;

    const rootStyles = css({
        width: fullWidth ? 'full' : 'fit',
    });

    return (
        <div className={rootStyles}>
            <Field.Root>
                {hasLabel && (
                    <Field.Label asChild htmlFor={textAreaId} className={labelStyles({ required })}>
                        <Type variant="inputHint">{label}</Type>
                    </Field.Label>
                )}
                <Field.Textarea
                    id={textAreaId}
                    className={cx(textAreaStyles({ variant, size, resize }), className)}
                    aria-describedby={hasError ? errorId : ariaDescribedBy}
                    aria-invalid={hasError}
                    required={required}
                    {...arkProps}
                />
            </Field.Root>

            {hasHelperText && <div className={helperTextStyles({ variant })}>{helperText}</div>}

            {hasError && (
                <div className={errorStyles({ variant })} id={errorId} role="alert" aria-live="polite">
                    {errorText}
                </div>
            )}
        </div>
    );
};
